import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { StatusDot } from "@/components/ui/status";
import { ToneBadge } from "@/features/oversight/oversight-ui";
import { cn } from "@/lib/utils";
import { FRESHNESS_ROWS } from "@/features/oversight/oversight-data";
import type { ProviderHealth, ChainHealth } from "@/features/oversight/oversight-data";

function providerPriority(p: ProviderHealth) {
  return p.statusTone === "success" ? 1 : 0;
}

function chainPriority(c: ChainHealth) {
  return c.statusTone === "success" ? 1 : 0;
}

export function ProviderChainHealth({ providers, chains }: { providers: ProviderHealth[]; chains: ChainHealth[] }) {
  const sortedProviders = [...providers].sort((a, b) => providerPriority(a) - providerPriority(b) || a.name.localeCompare(b.name));
  const sortedChains = [...chains].sort((a, b) => chainPriority(a) - chainPriority(b) || a.name.localeCompare(b.name));

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Provider Health</h2>
        <Link to="/monitoring" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          View all <ArrowUpRight className="size-3.5" />
        </Link>
      </div>

      <div className="flex flex-1 flex-col justify-between gap-5">
        <div className="space-y-1">
          {sortedProviders.map((p) => {
            const degraded = p.statusTone !== "success";
            return (
              <Link
                key={p.id}
                to="/monitoring/provider"
                className={cn(
                  "flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-bg",
                  degraded ? "bg-warning-soft/40 border border-warning-border" : "bg-bg"
                )}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <StatusDot tone={p.statusTone} />
                  <span className="truncate text-sm font-medium text-ink">{p.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="hidden font-mono text-xs text-muted sm:inline">{p.latencyP50}</span>
                  <ToneBadge tone={p.statusTone} dot={false}>
                    {p.statusLabel}
                  </ToneBadge>
                </div>
              </Link>
            );
          })}
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted">Network Chains</div>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {sortedChains.map((c) => {
              const degraded = c.statusTone !== "success";
              return (
                <Link
                  key={c.id}
                  to="/monitoring/chain"
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-bg",
                    degraded ? "bg-warning-soft/40 border border-warning-border" : "bg-bg"
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <StatusDot tone={c.statusTone} />
                    <span className="truncate text-sm font-medium text-ink">{c.name}</span>
                  </div>
                  <ToneBadge tone={c.statusTone} dot={false}>
                    {c.statusLabel}
                  </ToneBadge>
                </Link>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted">Source freshness</div>
          <div className="space-y-1">
            {FRESHNESS_ROWS.map((r) => (
              <div key={r.wallet} className="flex items-center justify-between gap-3 rounded-lg bg-bg px-3 py-2">
                <span className="truncate text-xs font-medium text-ink">{r.wallet}</span>
                <span className="shrink-0 text-xs text-muted">Balance {r.balance}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
